"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Layers, Zap, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, CircleX as XCircle, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  icon: UserCheck,
  title: "Direct Access to the Senior Strategist",
  freelance: "You work 100% directly with me. Every strategy, technical audit, and content recommendation is executed by my hands, not offloaded.",
  agency: "Pitched by senior directors, but day-to-day accounts are usually delegated to junior coordinators or rotating interns."
}, {
  icon: Layers,
  title: "Customised Strategy vs. Standardised Retainers",
  freelance: "I design bespoke SEO frameworks tailored specifically to your market positioning, search landscape, and actual commercial goals.",
  agency: "Locked into rigid package tiers with tick-box deliverables that often prioritize activity reports over real revenue impact."
}, {
  icon: Zap,
  title: "Agile Execution & Clear Communication",
  freelance: "No layers of account bureaucracy. We discuss an issue, prioritize the fix, and deploy technical changes immediately.",
  agency: "Changes take weeks to pass through account managers, department heads, and ticketing systems before implementation."
}, {
  icon: ShieldCheck,
  title: "Transparent Reporting & Accountability",
  freelance: "Direct accountability with raw data, honest tracking, and real search console insights without inflated vanity metrics.",
  agency: "Glossy PDF dashboards filled with vanity graphs that obscure what actual search progress has been achieved."
}];
const highlights = ["Direct phone, email, and strategy access", "Tailored search strategies built for commercial ROI", "No hidden retainers, bloated overheads, or middle management", "Practical, developer-friendly technical recommendations"];
export default function Comparison() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="freelancervsagencybenefits" data-nav="light" className="bg-background py-20 lg:py-28 relative overflow-hidden">
      {/* Subtle decorative background glow */}
      <div aria-hidden="true" className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-sans text-xs tracking-wide uppercase border border-border">
              <Sparkles className="w-3.5 h-3.5 text-accent mr-1" />
              Independent Advantage
            </Badge>
          </div>

          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight mb-6">
            Why Work Directly With Me Instead of an Agency
          </h2>

          <p className="font-sans text-muted-foreground text-base sm:text-lg leading-relaxed">
            When you hire an SEO agency, you often pay for office overhead and account managers. When you partner with me, you get direct senior consultancy, personal accountability, and agile organic growth.
          </p>
        </motion.div>

        {/* Comparison Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {comparisonPoints.map((point, index) => {
          const IconComponent = point.icon;
          return <motion.div key={point.title} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card/60 hover:bg-card hover:border-primary/30 transition-[background-color,border-color,box-shadow] duration-300 shadow-sm rounded-xl overflow-hidden p-6 sm:p-8 flex flex-col justify-between">
                  <CardContent className="p-0 space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-1">
                          {point.title}
                        </h3>
                      </div>
                    </div>

                    <div className="space-y-4 pt-2">
                      {/* Freelancer Advantage */}
                      <div className="rounded-lg bg-background p-4 border border-border">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          <span className="font-sans font-semibold text-xs tracking-wider uppercase text-primary">
                            Working With Me (Yassine)
                          </span>
                        </div>
                        <p className="font-sans text-sm text-foreground/90 leading-relaxed pl-6">
                          {point.freelance}
                        </p>
                      </div>

                      {/* Traditional Agency Reality */}
                      <div className="rounded-lg bg-muted/40 p-4 border border-border/50">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="font-sans font-medium text-xs tracking-wider uppercase text-muted-foreground">
                            Typical Agency Model
                          </span>
                        </div>
                        <p className="font-sans text-sm text-muted-foreground leading-relaxed pl-6">
                          {point.agency}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Banner Card */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="rounded-2xl border border-border bg-muted/50 p-8 sm:p-10 lg:p-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <Badge variant="outline" className="border-accent/40 text-foreground bg-accent/10 px-3 py-1 font-sans text-xs">
                Direct Partnership
              </Badge>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Ready for straightforward, senior-level SEO guidance?
              </h3>
              <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
                Discover my background, view verified client outcomes, or get in touch directly to explore how I can scale your organic presence.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {highlights.map((item, idx) => <div key={idx} data-index={idx} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3.5 justify-center items-stretch lg:items-end">
              <Button asChild size="lg" className="w-full sm:w-auto font-sans font-medium shadow-sm">
                <Link to="/AboutMePage" className="inline-flex items-center justify-center gap-2">
                  Learn More About My Background
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-sans font-medium">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  Book a Direct Consultation
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}